import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  InternalAxiosRequestConfig,
} from "axios"
import { ApiError, kindFromStatus } from "./ApiError"

// Cliente HTTP unico para todo el frontend. Se inyecta el token JWT por
// llamada (ver useApi) en lugar de fijar un Authorization global, asi cada
// request usa el token vigente que entrega Auth0 en ese momento.

const instance: AxiosInstance = axios.create({
  timeout: 30_000,
  headers: {
    "Content-Type": "application/json",
  },
})

instance.interceptors.request.use(
  (config: InternalAxiosRequestConfig): InternalAxiosRequestConfig => config,
  (error: AxiosError): Promise<never> => Promise.reject(toApiError(error)),
)

instance.interceptors.response.use(
  (response) => response,
  (error: AxiosError): Promise<never> => Promise.reject(toApiError(error)),
)

const toApiError = (error: AxiosError): ApiError => {
  const status = error.response?.status
  const kind = kindFromStatus(status)
  const data = error.response?.data
  const message =
    (typeof data === "object" && data && "message" in data
      ? String((data as { message: unknown }).message)
      : undefined) ||
    error.message ||
    "Error al realizar la solicitud"

  return new ApiError(message, kind, status, data)
}

export type RequestOptions = Omit<AxiosRequestConfig, "method" | "url" | "data">

export const httpClient = instance

export const httpRequest = async <T>(
  method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE",
  url: string,
  body?: unknown,
  options?: RequestOptions & { token?: string },
): Promise<T> => {
  const { token, headers, ...rest } = options ?? {}
  const response = await instance.request<T>({
    method,
    url,
    data: body,
    ...rest,
    headers: {
      ...(headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })

  if (response.status === 204) {
    return undefined as T
  }

  return response.data
}
